import { useEffect, useState } from 'react'
import { Card } from '../Common/Card'
import {
  ChevronDown, ChevronUp, Package, Wrench, Zap, MapPin, AlertTriangle,
  Eye, EyeOff, RefreshCw, Search, X, Lock, CheckCircle, Calculator, Percent, Database
} from 'lucide-react'
import { estimatesAPI, referencesAPI, gesnAPI } from '../../services/api'
import toast from 'react-hot-toast'

// ============ ОБЩИЕ ХЕЛПЕРЫ ============

const getRankColor = (rank) => {
  if (!rank || rank === 0) return 'bg-red-100 text-red-700'
  if (rank >= 0.8) return 'bg-green-100 text-green-700'
  if (rank >= 0.5) return 'bg-yellow-100 text-yellow-700'
  if (rank >= 0.3) return 'bg-orange-100 text-orange-700'
  return 'bg-red-100 text-red-700'
}

const itemCost = (item) => item?.total_cost ?? item?.total_price ?? 0

// Стоимость активных (неисключённых) позиций
const activeItemsCost = (items) =>
  (items || []).filter(i => !i.excluded).reduce((sum, i) => sum + itemCost(i), 0)

// Бейдж источника цены (площадка справочника): Минстрой, Ручной ввод и т.д.
const PriceSourceBadge = ({ item }) => {
  const src = item?.source || (item?.manual ? 'Ручной ввод' : null)
  if (!src) {
    return <span className="text-gray-400 text-xs">-</span>
  }
  const isManual = src === 'Ручной ввод'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${
      isManual ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-700'
    }`}>
      <Database size={10} />
      {src}
    </span>
  )
}

// ============ ПАНЕЛЬ РАСЧЁТА СМЕТ (регион + запуск поиска цен) ============

export const EstimateCalculationPanel = ({ cardId, hasEstimates, onCalculated }) => {
  const [regions, setRegions] = useState([])
  const [selectedRegion, setSelectedRegion] = useState('')
  const [markupPercent, setMarkupPercent] = useState(15)
  const [calculating, setCalculating] = useState(false)
  const [loadingRegions, setLoadingRegions] = useState(true)
  const [method, setMethod] = useState('catalog') // catalog | gesn
  const [gesnReady, setGesnReady] = useState(false)

  // Загружаем доступные регионы из справочников + статус ГЭСН
  useEffect(() => {
    const loadRegions = async () => {
      try {
        const response = await referencesAPI.getRegions()
        const regionsList = response.data || []
        setRegions(regionsList)
        // Автовыбор первого региона
        if (regionsList.length > 0 && !selectedRegion) {
          setSelectedRegion(regionsList[0].name)
        }
      } catch (error) {
        console.error('Load regions error:', error)
        toast.error('Не удалось загрузить список регионов')
      } finally {
        setLoadingRegions(false)
      }
    }
    const loadGesn = async () => {
      try {
        const response = await gesnAPI.currentBase()
        setGesnReady(response.data?.status === 'ready')
      } catch (error) {
        setGesnReady(false)
      }
    }
    loadRegions()
    loadGesn()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCalculate = async () => {
    if (!selectedRegion) {
      toast.error('Выберите регион для поиска цен')
      return
    }
    if (!cardId) {
      toast.error('Не указан ID проекта')
      return
    }

    setCalculating(true)
    try {
      if (method === 'gesn') {
        const response = await gesnAPI.calculate({
          card_id: cardId,
          region: selectedRegion || 'default',
          markup_percent: markupPercent,
          use_llm: true
        })
        const coverage = response.data?.coverage || {}
        toast.success(
          `ЛСР по ГЭСН: ${coverage.lines || 0} строк, авто ${coverage.auto || 0}, на проверку ${coverage.needs_review || 0}`
        )
        if (response.data?.warnings) {
          response.data.warnings.forEach(w => toast(w, { icon: '⚠️', duration: 5000 }))
        }
        onCalculated([response.data?.estimate].filter(Boolean))
      } else {
        const response = await estimatesAPI.calculateForCard({
          card_id: cardId,
          region: selectedRegion,
          markup_percent: markupPercent
        })

        const { estimates: newEstimates, count, warnings } = response.data

        if (count === 0) {
          toast.error('Не удалось рассчитать сметы. Проверьте, что листы проанализированы.')
        } else {
          toast.success(`Рассчитано смет: ${count} (регион: ${selectedRegion})`)
          if (warnings && warnings.length > 0) {
            warnings.forEach(w => toast(w, { icon: '⚠️', duration: 5000 }))
          }
        }

        onCalculated(newEstimates || [])
      }
    } catch (error) {
      console.error('Calculate estimates error:', error)
      toast.error(error.response?.data?.detail || 'Ошибка при расчёте смет')
    } finally {
      setCalculating(false)
    }
  }

  return (
    <Card>
{/*       <div className="flex items-center gap-2 mb-3"> */}
{/*         <Calculator size={20} className="text-blue-600" /> */}
{/*         <h3 className="text-lg font-semibold">Расчёт смет по справочникам</h3> */}
{/*       </div> */}
{/*       <p className="text-sm text-gray-600 mb-4"> */}
{/*         {hasEstimates */}
{/*           ? 'Пересчитать цены по другому региону. Незафиксированные сметы будут пересозданы.' */}
{/*           : 'Укажите регион и запустите поиск цен по позициям, извлечённым при анализе листов.'} */}
{/*       </p> */}
      <div className="flex flex-wrap items-end gap-3">
        {/* Метод расчёта */}
        <div className="w-56">
          <label className="block text-xs font-medium text-gray-700 mb-1">Метод</label>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            disabled={calculating}
            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="catalog">Справочники (ФССЦ/ФСЭМ)</option>
            <option value="gesn" disabled={!gesnReady}>
              {gesnReady ? 'ГЭСН / ЛСР' : 'ГЭСН (база не загружена)'}
            </option>
          </select>
        </div>
        {/* Выбор региона */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            <span className="inline-flex items-center gap-1">
              <MapPin size={12} />
              Регион
            </span>
          </label>
          {loadingRegions ? (
            <div className="px-3 py-2 border rounded-lg text-sm text-gray-400 bg-gray-50">
              Загрузка регионов...
            </div>
          ) : regions.length === 0 ? (
            <div className="px-3 py-2 border border-yellow-300 rounded-lg text-sm text-yellow-700 bg-yellow-50">
              Нет справочников. Загрузите их в настройках.
            </div>
          ) : (
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              disabled={calculating}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {regions.map((region) => (
                <option key={region.name} value={region.name}>
                  {region.name} ({region.materials_count || 0} матер., {region.equipment_count || 0} оборуд.)
                </option>
              ))}
            </select>
          )}
        </div>

{/*          */}{/* Наценка */}
{/*         <div className="w-32"> */}
{/*           <label className="block text-xs font-medium text-gray-700 mb-1"> */}
{/*             <span className="inline-flex items-center gap-1"> */}
{/*               <Percent size={12} /> */}
{/*               Наценка, % */}
{/*             </span> */}
{/*           </label> */}
{/*           <input */}
{/*             type="number" */}
{/*             min="0" */}
{/*             max="100" */}
{/*             value={markupPercent} */}
{/*             onChange={(e) => setMarkupPercent(Number(e.target.value))} */}
{/*             disabled={calculating} */}
{/*             className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" */}
{/*           /> */}
{/*         </div> */}

        {/* Кнопка расчёта */}
        <button
          onClick={handleCalculate}
          disabled={calculating || loadingRegions || (method === 'catalog' && regions.length === 0) || (method === 'gesn' && !gesnReady)}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
        >
          {calculating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Поиск цен...
            </>
          ) : (
            <>
              <Search size={16} />
              {hasEstimates ? 'Пересчитать' : 'Рассчитать'}
            </>
          )}
        </button>
      </div>
    </Card>
  )
}

// ============ ГЛАВНЫЙ КОМПОНЕНТ ============

export const EstimateList = ({ estimates, cardId }) => {
  const [expandedEstimate, setExpandedEstimate] = useState(null)
  // Локальная копия смет - обновляется после API-операций (исключение/замена/расчёт)
  const [localEstimates, setLocalEstimates] = useState(estimates || [])

  useEffect(() => {
    setLocalEstimates(estimates || [])
  }, [estimates])

  // Обновление одной сметы после ответа сервера
  const handleEstimateUpdate = (estimateId, updatedFields) => {
    setLocalEstimates(prev =>
      prev.map(est =>
        est.id === estimateId ? { ...est, ...updatedFields } : est
      )
    )
  }

  // После расчёта смет с регионом - заменяем незафиксированные сметы новыми
  const handleCalculated = (newEstimates) => {
    setLocalEstimates(prev => {
      const fixedEstimates = prev.filter(e => e.is_fixed)
      return [...fixedEstimates, ...newEstimates]
    })
  }

  const hasEstimates = localEstimates && localEstimates.length > 0

  if (!hasEstimates) {
    return (
      <div className="space-y-4">
        {/* Панель расчёта доступна даже без смет - сметы создаются именно здесь */}
        {cardId && (
          <EstimateCalculationPanel
            cardId={cardId}
            hasEstimates={false}
            onCalculated={handleCalculated}
          />
        )}
        <Card>
          <p className="text-center text-gray-600">
            Сметы ещё не рассчитаны. {cardId ? 'Укажите регион и запустите расчёт.' : ''}
          </p>
        </Card>
      </div>
    )
  }

  const getEstimateTypeLabel = (type) => {
    const labels = {
      'materials': 'Материалы',
      'equipment': 'Оборудование',
      'operations': 'Операции',
      'gesn': 'ЛСР (ГЭСН)',
    }
    return labels[type] || type
  }

  const getEstimateIcon = (type) => {
    switch (type) {
      case 'materials':
        return <Package size={20} className="text-blue-600" />
      case 'equipment':
        return <Wrench size={20} className="text-orange-600" />
      case 'operations':
        return <Zap size={20} className="text-purple-600" />
      case 'gesn':
        return <Zap size={20} className="text-emerald-600" />
      default:
        return null
    }
  }

  const renderEstimateContent = (estimate) => {
    switch (estimate.estimate_type) {
      case 'materials':
        return (
          <MaterialsEstimateTable
            estimate={estimate}
            onEstimateUpdate={handleEstimateUpdate}
          />
        )
      case 'equipment':
        return (
          <EquipmentEstimateTable
            estimate={estimate}
            onEstimateUpdate={handleEstimateUpdate}
          />
        )
      case 'operations':
        return (
          <OperationsEstimateTable
            estimate={estimate}
            onEstimateUpdate={handleEstimateUpdate}
          />
        )
      default:
        return <DefaultEstimateTable estimate={estimate} />
    }
  }

  return (
    <div className="space-y-4">
      {/* Панель пересчёта смет с выбором региона */}
      {cardId && (
        <EstimateCalculationPanel
          cardId={cardId}
          hasEstimates={true}
          onCalculated={handleCalculated}
        />
      )}

      {localEstimates.map((estimate) => {
        const excludedCount = (estimate.data?.items || []).filter(i => i.excluded).length

        return (
          <Card key={estimate.id}>
            <div
              onClick={() =>
                setExpandedEstimate(
                  expandedEstimate === estimate.id ? null : estimate.id
                )
              }
              className="cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  {getEstimateIcon(estimate.estimate_type)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold">
                        {getEstimateTypeLabel(estimate.estimate_type)}
                      </h3>
                      {estimate.is_fixed && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full">
                          <Lock size={12} />
                          Зафиксирована
                        </span>
                      )}
                      {estimate.data?.region && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          <MapPin size={12} />
                          {estimate.data.region}
                        </span>
                      )}
                      {(estimate.data?.price_sources || []).map((src) => (
                        <span
                          key={src}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full"
                          title="Источник цен"
                        >
                          <Database size={12} />
                          {src}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <p className="text-sm text-gray-600">
                        {getEstimateItemCount(estimate)} позиций
                      </p>
                      {excludedCount > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                          <EyeOff size={12} />
                          {excludedCount} исключено
                        </span>
                      )}
                      {estimate.data?.not_found_count > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                          <AlertTriangle size={12} />
                          {estimate.data.not_found_count} не найдено
                        </span>
                      )}
                      {estimate.data?.low_rank_count > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">
                          <AlertTriangle size={12} />
                          {estimate.data.low_rank_count} низкая точность
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">
                      {estimate.total_cost?.toLocaleString('ru-RU')} ₽
                    </p>
                  </div>
                  {expandedEstimate === estimate.id ? (
                    <ChevronUp size={24} />
                  ) : (
                    <ChevronDown size={24} />
                  )}
                </div>
              </div>
            </div>

            {/* Развернутая информация */}
            {expandedEstimate === estimate.id && (
              <div className="mt-4 pt-4 border-t">
                {renderEstimateContent(estimate)}
              </div>
            )}
          </Card>
        )
      })}
    </div>
  )
}

// Вспомогательная функция для подсчета позиций
const getEstimateItemCount = (estimate) => {
  if (estimate.data?.items) {
    return estimate.data.items.length
  }
  if (estimate.data?.assembly) {
    return Array.isArray(estimate.data.assembly) ? estimate.data.assembly.length : 1
  }
  if (estimate.data?.assembly_steps) {
    return estimate.data.assembly_steps.length
  }
  return 0
}

// ============ МОДАЛЬНОЕ ОКНО ЗАМЕНЫ ПОЗИЦИИ ============

export const ReplaceItemModal = ({
  isOpen,
  onClose,
  estimate,
  itemType,       // 'materials' | 'equipment'
  itemIndex,
  currentItem,
  onReplaced      // callback(updatedEstimateFields)
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [alternatives, setAlternatives] = useState([])
  const [searching, setSearching] = useState(false)
  const [replacing, setReplacing] = useState(false)
  const [searched, setSearched] = useState(false)

  // При открытии модалки - подставляем название текущей позиции и сразу ищем
  useEffect(() => {
    if (isOpen && currentItem) {
      const initialQuery = currentItem.search_query || currentItem.name || ''
      setSearchQuery(initialQuery)
      setAlternatives([])
      setSearched(false)
      if (initialQuery) {
        performSearch(initialQuery)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentItem])

  const performSearch = async (query) => {
    if (!query || !query.trim()) {
      toast.error('Введите поисковый запрос')
      return
    }

    setSearching(true)
    try {
      const response = await estimatesAPI.searchAlternatives(estimate.id, {
        item_type: itemType,
        query: query.trim(),
        region: estimate.data?.region || estimate.region || null,
        limit: 10
      })
      setAlternatives(response.data.alternatives || [])
      setSearched(true)
    } catch (error) {
      console.error('Search alternatives error:', error)
      toast.error(error.response?.data?.detail || 'Ошибка поиска альтернатив')
    } finally {
      setSearching(false)
    }
  }

  const handleSelect = async (alternative) => {
    setReplacing(true)
    try {
      const response = await estimatesAPI.replaceItem(estimate.id, {
        item_type: itemType,
        item_index: itemIndex,
        new_item_id: alternative.id
      })

      const updated = response.data.estimate
      onReplaced({
        total_cost: updated.total_cost,
        data: updated.data,
        items_override: updated.items_override
      })
      toast.success(`Позиция заменена на «${alternative.name}»`)
      onClose()
    } catch (error) {
      console.error('Replace item error:', error)
      toast.error(error.response?.data?.detail || 'Ошибка при замене позиции')
    } finally {
      setReplacing(false)
    }
  }

  if (!isOpen) return null

  const priceLabel = itemType === 'equipment' ? 'Цена/час' : 'Цена/ед.'
  const getPrice = (alt) => itemType === 'equipment' ? alt.price_per_hour : alt.price

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[85vh] flex flex-col">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <RefreshCw size={20} className="text-blue-600" />
            Замена позиции
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            disabled={replacing}
          >
            <X size={20} />
          </button>
        </div>

        {/* Текущая позиция */}
        <div className="p-4 bg-gray-50 border-b">
          <p className="text-xs text-gray-500 mb-1">Текущая позиция:</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">{currentItem?.name}</p>
              <p className="text-xs text-gray-600">
                {currentItem?.code && <span>Код: {currentItem.code} · </span>}
                {itemType === 'equipment'
                  ? `${currentItem?.price_per_hour?.toLocaleString('ru-RU') || 0} ₽/час`
                  : `${currentItem?.unit_price?.toLocaleString('ru-RU') || 0} ₽/${currentItem?.unit || 'ед.'}`}
              </p>
            </div>
            {currentItem?.rank !== undefined && currentItem?.rank !== null && (
              <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${getRankColor(currentItem.rank)}`}>
                Точность: {(currentItem.rank * 100).toFixed(0)}%
              </span>
            )}
          </div>
        </div>

        {/* Поиск */}
        <div className="p-4 border-b">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && performSearch(searchQuery)}
              placeholder="Поиск по справочнику (название, код, ГОСТ)..."
              className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              disabled={searching || replacing}
            />
            <button
              onClick={() => performSearch(searchQuery)}
              disabled={searching || replacing}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
            >
              {searching ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <Search size={16} />
              )}
              Найти
            </button>
          </div>
        </div>

        {/* Результаты поиска */}
        <div className="flex-1 overflow-y-auto p-4">
          {searching && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          )}

          {!searching && searched && alternatives.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <AlertTriangle size={32} className="mx-auto mb-2 text-yellow-500" />
              <p>Ничего не найдено. Попробуйте изменить запрос.</p>
            </div>
          )}

          {!searching && alternatives.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 mb-2">
                Найдено {alternatives.length} вариантов (отсортировано по релевантности):
              </p>
              {alternatives.map((alt) => (
                <div
                  key={alt.id}
                  className="border rounded-lg p-3 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-wrap text-gray-900">{alt.name}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {alt.code && (
                          <span className="text-xs text-gray-500">Код: {alt.code}</span>
                        )}
                        {alt.gost_part && (
                          <span className="text-xs text-gray-500">ГОСТ: {alt.gost_part}</span>
                        )}
                        {alt.category && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                            {alt.category}
                          </span>
                        )}
                        {alt.region && (
                          <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                            <MapPin size={10} />
                            {alt.region}
                          </span>
                        )}
                        {alt.source && (
                          <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">
                            <Database size={10} />
                            {alt.source}
                          </span>
                        )}
                        <span className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded ${getRankColor(alt.rank)}`}>
                          {(alt.rank * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-semibold text-green-600">
                        {getPrice(alt)?.toLocaleString('ru-RU')} ₽
                      </p>
                      <p className="text-xs text-gray-500">{priceLabel}</p>
                    </div>
                    <button
                      onClick={() => handleSelect(alt)}
                      disabled={replacing}
                      className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-1 flex-shrink-0"
                    >
                      <CheckCircle size={14} />
                      Выбрать
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============ КНОПКИ ДЕЙСТВИЙ ДЛЯ ПОЗИЦИИ ============

const ItemActions = ({ item, isFixed, togglingIndex, index, onToggleExclusion, onOpenReplace }) => {
  if (isFixed) {
    return <span className="text-gray-400 text-xs">—</span>
  }

  return (
    <div className="flex items-center justify-end gap-1">
      {/* Заменить */}
      {!item.excluded && (
        <button
          type="button"
          onClick={() => onOpenReplace(index)}
          title="Заменить из справочника"
          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
        >
          <RefreshCw size={16} />
        </button>
      )}
      {/* Исключить / Включить */}
      <button
        type="button"
        onClick={() => onToggleExclusion(index, !item.excluded)}
        disabled={togglingIndex === index}
        title={item.excluded ? 'Включить обратно в смету' : 'Исключить из сметы'}
        className={`p-1.5 rounded transition-colors disabled:opacity-50 ${
          item.excluded
            ? 'text-green-600 hover:bg-green-50'
            : 'text-gray-500 hover:bg-gray-100'
        }`}
      >
        {togglingIndex === index ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
        ) : item.excluded ? (
          <Eye size={16} />
        ) : (
          <EyeOff size={16} />
        )}
      </button>
    </div>
  )
}

// ============ МАТЕРИАЛЫ ============

const MaterialsEstimateTable = ({ estimate, onEstimateUpdate }) => {
  const [togglingIndex, setTogglingIndex] = useState(null)
  const [replaceModal, setReplaceModal] = useState({ open: false, index: null })

  const items = estimate.data?.items || []
  const isFixed = !!estimate.is_fixed

  const handleToggleExclusion = async (index, excluded) => {
    setTogglingIndex(index)
    try {
      const response = await estimatesAPI.toggleItemExclusion(estimate.id, {
        item_type: 'materials',
        item_index: index,
        excluded
      })
      const updated = response.data.estimate
      onEstimateUpdate(estimate.id, {
        total_cost: updated.total_cost,
        data: updated.data,
        items_override: updated.items_override
      })
      toast.success(excluded ? 'Позиция исключена из сметы' : 'Позиция включена обратно')
    } catch (error) {
      console.error('Toggle exclusion error:', error)
      toast.error(error.response?.data?.detail || 'Ошибка при изменении позиции')
    } finally {
      setTogglingIndex(null)
    }
  }

  const handleReplaced = (updatedFields) => {
    onEstimateUpdate(estimate.id, updatedFields)
  }

  if (!items || items.length === 0) {
    return <p className="text-gray-600">Позиции отсутствуют</p>
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left py-3 px-3 font-semibold">Материал</th>
              <th className="text-center py-3 px-3 font-semibold">Код</th>
              <th className="text-center py-3 px-3 font-semibold">ГОСТ изделия</th>
              <th className="text-center py-3 px-3 font-semibold">ГОСТ материала</th>
              <th className="text-center py-3 px-3 font-semibold">Кол-во</th>
              <th className="text-center py-3 px-3 font-semibold">Ед. изм.</th>
              <th className="text-center py-3 px-3 font-semibold">Регион</th>
              <th className="text-center py-3 px-3 font-semibold">Ист. цены</th>
              <th className="text-center py-3 px-3 font-semibold">Точность</th>
              <th className="text-right py-3 px-3 font-semibold">Цена/ед.</th>
              <th className="text-right py-3 px-3 font-semibold">Сумма</th>
              <th className="text-right py-3 px-3 font-semibold">Действия</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr
                key={index}
                className={`border-b transition-colors ${
                  item.excluded
                    ? 'bg-gray-50 opacity-50'
                    : 'hover:bg-gray-50'
                }`}
              >
                <td className="py-3 px-3">
                  <div className="flex flex-col">
                    <span className={`font-medium text-gray-900 ${item.excluded ? 'line-through' : ''}`}>
                      {item.name}
                      {item.replaced && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded no-underline inline-block">
                          заменено
                        </span>
                      )}
                      {item.excluded && (
                        <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded no-underline inline-block">
                          исключено
                        </span>
                      )}
                    </span>
                    {item.warning && !item.excluded && (
                      <span className="text-xs text-red-600 flex items-center gap-1 mt-1">
                        <AlertTriangle size={12} />
                        {item.warning}
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-center py-3 px-3 text-gray-600 text-xs">
                  {item.code || '-'}
                </td>
                <td className="text-center py-3 px-3 text-gray-600 text-xs">
                  {item.gost_part || '-'}
                </td>
                <td className="text-center py-3 px-3 text-gray-600 text-xs">
                  {item.gost_material || '-'}
                </td>
                <td className="text-center py-3 px-3 font-medium">{item.quantity}</td>
                <td className="text-center py-3 px-3 text-gray-600">{item.unit || '-'}</td>
                <td className="text-center py-3 px-3">
                  {item.region ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                      <MapPin size={10} />
                      {item.region}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">-</span>
                  )}
                </td>
                <td className="text-center py-3 px-3">
                  <PriceSourceBadge item={item} />
                </td>
                <td className="text-center py-3 px-3">
                  {item.rank !== undefined && item.rank !== null ? (
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${getRankColor(item.rank)}`}>
                      {(item.rank * 100).toFixed(0)}%
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">-</span>
                  )}
                </td>
                <td className="text-right py-3 px-3 text-gray-900">
                  {item.unit_price?.toLocaleString('ru-RU') || '0'} ₽
                </td>
                <td className={`text-right py-3 px-3 font-semibold ${item.excluded ? 'text-gray-400 line-through' : 'text-green-600'}`}>
                  {itemCost(item).toLocaleString('ru-RU')} ₽
                </td>
                <td className="text-right py-3 px-3">
                  <ItemActions
                    item={item}
                    isFixed={isFixed}
                    togglingIndex={togglingIndex}
                    index={index}
                    onToggleExclusion={handleToggleExclusion}
                    onOpenReplace={(idx) => setReplaceModal({ open: true, index: idx })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-100 font-bold">
              <td colSpan="10" className="text-right py-3 px-3">
                Итого:
              </td>
              <td className="text-right py-3 px-3 text-green-600">
                {estimate.total_cost?.toLocaleString('ru-RU')} ₽
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Статистика материалов */}
      <MaterialsStatistics estimate={estimate} />

      {/* Модальное окно замены */}
      <ReplaceItemModal
        isOpen={replaceModal.open}
        onClose={() => setReplaceModal({ open: false, index: null })}
        estimate={estimate}
        itemType="materials"
        itemIndex={replaceModal.index}
        currentItem={replaceModal.index !== null ? items[replaceModal.index] : null}
        onReplaced={handleReplaced}
      />
    </div>
  )
}

const MaterialsStatistics = ({ estimate }) => {
  const allItems = estimate.data?.items || []
  const items = allItems.filter(i => !i.excluded)
  const excludedCount = allItems.length - items.length
  const activeCost = activeItemsCost(allItems)
  const avgPrice = items.length > 0 ? activeCost / items.length : 0
  const maxPrice = Math.max(...items.map(itemCost), 0)
  const minPrice = items.length > 0 ? Math.min(...items.map(itemCost)) : 0

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t">
      <div className="bg-blue-50 p-3 rounded-lg">
        <p className="text-xs text-gray-600">Активных позиций</p>
        <p className="text-xl font-bold text-blue-600">
          {items.length}
          {excludedCount > 0 && (
            <span className="text-sm text-gray-500 font-normal"> (+{excludedCount} искл.)</span>
          )}
        </p>
      </div>
      <div className="bg-purple-50 p-3 rounded-lg">
        <p className="text-xs text-gray-600">Средняя цена</p>
        <p className="text-xl font-bold text-purple-600">
          {avgPrice.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
        </p>
      </div>
      <div className="bg-orange-50 p-3 rounded-lg">
        <p className="text-xs text-gray-600">Самый дорогой</p>
        <p className="text-xl font-bold text-orange-600">
          {maxPrice.toLocaleString('ru-RU')} ₽
        </p>
      </div>
      <div className="bg-indigo-50 p-3 rounded-lg">
        <p className="text-xs text-gray-600">Самый дешевый</p>
        <p className="text-xl font-bold text-indigo-600">
          {minPrice.toLocaleString('ru-RU')} ₽
        </p>
      </div>
    </div>
  )
}

// ============ ОБОРУДОВАНИЕ (ПРЯМОЙ СПИСОК) ============

const EquipmentEstimateTable = ({ estimate, onEstimateUpdate }) => {
  const [togglingIndex, setTogglingIndex] = useState(null)
  const [replaceModal, setReplaceModal] = useState({ open: false, index: null })

  const items = estimate.data?.equipment?.length
    ? estimate.data.equipment
    : (estimate.data?.items || [])
  const isFixed = !!estimate.is_fixed

  const handleToggleExclusion = async (index, excluded) => {
    setTogglingIndex(index)
    try {
      const response = await estimatesAPI.toggleItemExclusion(estimate.id, {
        item_type: 'equipment',
        item_index: index,
        excluded
      })
      const updated = response.data.estimate
      onEstimateUpdate(estimate.id, {
        total_cost: updated.total_cost,
        data: updated.data,
        items_override: updated.items_override
      })
      toast.success(excluded ? 'Позиция исключена из сметы' : 'Позиция включена обратно')
    } catch (error) {
      console.error('Toggle exclusion error:', error)
      toast.error(error.response?.data?.detail || 'Ошибка при изменении позиции')
    } finally {
      setTogglingIndex(null)
    }
  }

  const handleReplaced = (updatedFields) => {
    onEstimateUpdate(estimate.id, updatedFields)
  }

  // Данные сборки (компоненты и этапы) - отображаются вместе с оборудованием
  const components = estimate.data?.components || []
  const assemblySteps = estimate.data?.assembly_steps || []
  const assemblyName = estimate.data?.assembly_name || ''

  if ((!items || items.length === 0) && components.length === 0 && assemblySteps.length === 0) {
    return <p className="text-gray-600">Данные об оборудовании отсутствуют</p>
  }

  return (
    <div className="space-y-4">
      {/* Название сборки */}
      {assemblyName && (
        <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <Wrench size={18} className="text-orange-600" />
          <span className="font-semibold text-gray-900">Сборка: {assemblyName}</span>
        </div>
      )}

      {/* Компоненты сборки */}
      {components.length > 0 && (
        <div>
          <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Package size={16} className="text-blue-600" />
            Компоненты сборки ({components.length})
          </h5>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-2 px-3 font-semibold">Обозначение</th>
                  <th className="text-left py-2 px-3 font-semibold">Наименование</th>
                  <th className="text-center py-2 px-3 font-semibold">Кол-во</th>
                </tr>
              </thead>
              <tbody>
                {components.map((component, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3 text-xs font-mono text-gray-600">
                      {component.designation || '-'}
                    </td>
                    <td className="py-2 px-3 font-medium">{component.name}</td>
                    <td className="text-center py-2 px-3">{component.quantity ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Оборудование с ценами */}
      {items.length > 0 && (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left py-3 px-3 font-semibold">Оборудование</th>
              <th className="text-center py-3 px-3 font-semibold">Код</th>
              <th className="text-center py-3 px-3 font-semibold">Категория</th>
              <th className="text-center py-3 px-3 font-semibold">Регион</th>
              <th className="text-center py-3 px-3 font-semibold">Ист. цены</th>
              <th className="text-center py-3 px-3 font-semibold">Точность</th>
              <th className="text-center py-3 px-3 font-semibold">Часов</th>
              <th className="text-right py-3 px-3 font-semibold">Цена/час</th>
              <th className="text-right py-3 px-3 font-semibold">Сумма</th>
              <th className="text-right py-3 px-3 font-semibold">Действия</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr
                key={index}
                className={`border-b transition-colors ${
                  item.excluded
                    ? 'bg-gray-50 opacity-50'
                    : 'hover:bg-gray-50'
                }`}
              >
                <td className="py-3 px-3">
                  <div className="flex flex-col">
                    <span className={`font-medium text-gray-900 ${item.excluded ? 'line-through' : ''}`}>
                      {item.name}
                      {item.replaced && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded inline-block">
                          заменено
                        </span>
                      )}
                      {item.excluded && (
                        <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded inline-block">
                          исключено
                        </span>
                      )}
                    </span>
                    {item.warning && !item.excluded && (
                      <span className="text-xs text-red-600 flex items-center gap-1 mt-1">
                        <AlertTriangle size={12} />
                        {item.warning}
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-center py-3 px-3 text-gray-600 text-xs font-mono">
                  {item.code || '-'}
                </td>
                <td className="text-center py-3 px-3">
                  <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-medium">
                    {item.category || 'unknown'}
                  </span>
                </td>
                <td className="text-center py-3 px-3">
                  {item.region ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                      <MapPin size={10} />
                      {item.region}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">-</span>
                  )}
                </td>
                <td className="text-center py-3 px-3">
                  <PriceSourceBadge item={item} />
                </td>
                <td className="text-center py-3 px-3">
                  {item.rank !== undefined && item.rank !== null ? (
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${getRankColor(item.rank)}`}>
                      {(item.rank * 100).toFixed(0)}%
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">-</span>
                  )}
                </td>
                <td className="text-center py-3 px-3 font-medium">
                  {item.hours}
                </td>
                <td className="text-right py-3 px-3 text-gray-900">
                  {item.price_per_hour?.toLocaleString('ru-RU') || '0'} ₽
                </td>
                <td className={`text-right py-3 px-3 font-semibold ${item.excluded ? 'text-gray-400 line-through' : 'text-green-600'}`}>
                  {itemCost(item).toLocaleString('ru-RU')} ₽
                </td>
                <td className="text-right py-3 px-3">
                  <ItemActions
                    item={item}
                    isFixed={isFixed}
                    togglingIndex={togglingIndex}
                    index={index}
                    onToggleExclusion={handleToggleExclusion}
                    onOpenReplace={(idx) => setReplaceModal({ open: true, index: idx })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-100 font-bold">
              <td colSpan="8" className="text-right py-3 px-3">
                Итого:
              </td>
              <td className="text-right py-3 px-3 text-green-600">
                {estimate.total_cost?.toLocaleString('ru-RU')} ₽
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      )}

      {/* Этапы сборки */}
      {assemblySteps.length > 0 && (
        <div>
          <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Zap size={16} className="text-purple-600" />
            Этапы сборки ({assemblySteps.length})
          </h5>
          <div className="space-y-2">
            {assemblySteps.map((step, idx) => (
              <div key={idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-7 w-7 rounded-full bg-purple-600 text-white font-bold text-xs">
                    {step.order ?? idx + 1}
                  </div>
                </div>
                <p className="text-sm text-gray-900 flex-1">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Модальное окно замены */}
      <ReplaceItemModal
        isOpen={replaceModal.open}
        onClose={() => setReplaceModal({ open: false, index: null })}
        estimate={estimate}
        itemType="equipment"
        itemIndex={replaceModal.index}
        currentItem={replaceModal.index !== null ? items[replaceModal.index] : null}
        onReplaced={handleReplaced}
      />
    </div>
  )
}

// ============ ОПЕРАЦИИ ============

const OperationsEstimateTable = ({ estimate, onEstimateUpdate }) => {
  const [togglingIndex, setTogglingIndex] = useState(null)
  const [replaceModal, setReplaceModal] = useState({ open: false, index: null })

  // Операции: новый формат в data.operations, старый - в data.items
  const operations = estimate.data?.operations?.length
    ? estimate.data.operations
    : (estimate.data?.items || [])
  // Оборудование, найденное для операций (с ценами из справочника)
  const equipment = estimate.data?.equipment || []
  const isFixed = !!estimate.is_fixed

  const handleToggleExclusion = async (index, excluded) => {
    setTogglingIndex(index)
    try {
      const response = await estimatesAPI.toggleItemExclusion(estimate.id, {
        item_type: 'equipment',
        item_index: index,
        excluded
      })
      const updated = response.data.estimate
      onEstimateUpdate?.(estimate.id, {
        total_cost: updated.total_cost,
        data: updated.data,
        items_override: updated.items_override
      })
      toast.success(excluded ? 'Позиция исключена из сметы' : 'Позиция включена обратно')
    } catch (error) {
      console.error('Toggle exclusion error:', error)
      toast.error(error.response?.data?.detail || 'Ошибка при изменении позиции')
    } finally {
      setTogglingIndex(null)
    }
  }

  const handleReplaced = (updatedFields) => {
    onEstimateUpdate?.(estimate.id, updatedFields)
  }

  if (operations.length === 0 && equipment.length === 0) {
    return <p className="text-gray-600">Операции отсутствуют</p>
  }

  return (
    <div className="space-y-4">
      {/* Таблица операций */}
      {operations.length > 0 && (
        <div>
          <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Zap size={16} className="text-purple-600" />
            Операции ({operations.length})
          </h5>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-center py-3 px-3 font-semibold">№</th>
                  <th className="text-left py-3 px-3 font-semibold">Операция</th>
                  <th className="text-center py-3 px-3 font-semibold">Тип</th>
                  <th className="text-center py-3 px-3 font-semibold">Время, ч</th>
                  <th className="text-left py-3 px-3 font-semibold">Оборудование</th>
                </tr>
              </thead>
              <tbody>
                {operations.map((item, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="text-center py-3 px-3 text-gray-600">
                      {item.operation_number || index + 1}
                    </td>
                    <td className="py-3 px-3 font-medium text-gray-900">
                      {item.name || item.description || '-'}
                    </td>
                    <td className="text-center py-3 px-3">
                      <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-medium">
                        {item.operation_type || item.type || '-'}
                      </span>
                    </td>
                    <td className="text-center py-3 px-3 font-medium">
                      {item.time ?? item.quantity ?? '-'}
                    </td>
                    <td className="py-3 px-3 text-gray-600 text-xs">
                      {item.equipment || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Оборудование для операций (с ценами) */}
      {equipment.length > 0 && (
        <div>
          <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Wrench size={16} className="text-orange-600" />
            Оборудование для операций ({equipment.length})
          </h5>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-3 font-semibold">Оборудование</th>
                  <th className="text-left py-3 px-3 font-semibold">Операция</th>
                  <th className="text-center py-3 px-3 font-semibold">Регион</th>
                  <th className="text-center py-3 px-3 font-semibold">Ист. цены</th>
                  <th className="text-center py-3 px-3 font-semibold">Точность</th>
                  <th className="text-center py-3 px-3 font-semibold">Часов</th>
                  <th className="text-right py-3 px-3 font-semibold">Цена/час</th>
                  <th className="text-right py-3 px-3 font-semibold">Сумма</th>
                  <th className="text-right py-3 px-3 font-semibold">Действия</th>
                </tr>
              </thead>
              <tbody>
                {equipment.map((item, index) => (
                  <tr
                    key={index}
                    className={`border-b transition-colors ${
                      item.excluded ? 'bg-gray-50 opacity-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="py-3 px-3">
                      <div className="flex flex-col">
                        <span className={`font-medium text-gray-900 ${item.excluded ? 'line-through' : ''}`}>
                          {item.name}
                          {item.replaced && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded inline-block">
                              заменено
                            </span>
                          )}
                          {item.excluded && (
                            <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded inline-block">
                              исключено
                            </span>
                          )}
                        </span>
                        {item.warning && !item.excluded && (
                          <span className="text-xs text-red-600 flex items-center gap-1 mt-1">
                            <AlertTriangle size={12} />
                            {item.warning}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-gray-600 text-xs">
                      {item.operation_name || '-'}
                    </td>
                    <td className="text-center py-3 px-3">
                      {item.region ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                          <MapPin size={10} />
                          {item.region}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="text-center py-3 px-3">
                      <PriceSourceBadge item={item} />
                    </td>
                    <td className="text-center py-3 px-3">
                      {item.rank !== undefined && item.rank !== null ? (
                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${getRankColor(item.rank)}`}>
                          {(item.rank * 100).toFixed(0)}%
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="text-center py-3 px-3 font-medium">{item.hours}</td>
                    <td className="text-right py-3 px-3 text-gray-900">
                      {item.price_per_hour?.toLocaleString('ru-RU') || '0'} ₽
                    </td>
                    <td className={`text-right py-3 px-3 font-semibold ${item.excluded ? 'text-gray-400 line-through' : 'text-green-600'}`}>
                      {itemCost(item).toLocaleString('ru-RU')} ₽
                    </td>
                    <td className="text-right py-3 px-3">
                      <ItemActions
                        item={item}
                        isFixed={isFixed}
                        togglingIndex={togglingIndex}
                        index={index}
                        onToggleExclusion={handleToggleExclusion}
                        onOpenReplace={(idx) => setReplaceModal({ open: true, index: idx })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 font-bold">
                  <td colSpan="7" className="text-right py-3 px-3">
                    Итого:
                  </td>
                  <td className="text-right py-3 px-3 text-green-600">
                    {estimate.total_cost?.toLocaleString('ru-RU')} ₽
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Модальное окно замены оборудования */}
      <ReplaceItemModal
        isOpen={replaceModal.open}
        onClose={() => setReplaceModal({ open: false, index: null })}
        estimate={estimate}
        itemType="equipment"
        itemIndex={replaceModal.index}
        currentItem={replaceModal.index !== null ? equipment[replaceModal.index] : null}
        onReplaced={handleReplaced}
      />

      {/* Статистика операций */}
      <OperationsStatistics estimate={estimate} />
    </div>
  )
}

const OperationsStatistics = ({ estimate }) => {
  // Новый формат: операции в data.operations, оборудование с ценами в data.equipment
  const items = estimate.data?.operations?.length
    ? estimate.data.operations
    : (estimate.data?.items || [])
  const equipment = (estimate.data?.equipment || []).filter(e => !e.excluded)
  const avgPrice = equipment.length > 0 ? estimate.total_cost / equipment.length : 0
  const maxPrice = Math.max(...equipment.map(itemCost), 0)
  // Время: поле time (часы) в новом формате, time_per_unit*quantity - в старом
  const totalTime = items.reduce(
    (sum, item) => sum + (item.time ?? ((item.time_per_unit || 0) * (item.quantity || 1))),
    0
  )

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t">
      <div className="bg-blue-50 p-3 rounded-lg">
        <p className="text-xs text-gray-600">Всего операций</p>
        <p className="text-xl font-bold text-blue-600">{items.length}</p>
      </div>
      <div className="bg-purple-50 p-3 rounded-lg">
        <p className="text-xs text-gray-600">Общее время</p>
        <p className="text-xl font-bold text-purple-600">
          {totalTime.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ч
        </p>
      </div>
      <div className="bg-orange-50 p-3 rounded-lg">
        <p className="text-xs text-gray-600">Средняя цена оборуд.</p>
        <p className="text-xl font-bold text-orange-600">
          {avgPrice.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
        </p>
      </div>
      <div className="bg-indigo-50 p-3 rounded-lg">
        <p className="text-xs text-gray-600">Самое дорогое оборуд.</p>
        <p className="text-xl font-bold text-indigo-600">
          {maxPrice.toLocaleString('ru-RU')} ₽
        </p>
      </div>
    </div>
  )
}

// ============ ПО УМОЛЧАНИЮ ============

const DefaultEstimateTable = ({ estimate }) => {
  if (!estimate.data?.assembly_steps || estimate.data.assembly_steps.length === 0) {
    return <p className="text-gray-600">Позиции отсутствуют</p>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="text-left py-3 px-3 font-semibold">Наименование</th>
            <th className="text-center py-3 px-3 font-semibold">Описание</th>
          </tr>
        </thead>
        <tbody>
          {estimate.data.assembly_steps.map((step, index) => (
            <tr key={index} className="border-b hover:bg-gray-50">
              <td className="py-3 px-3 font-medium">{step.order}</td>
              <td className="py-3 px-3">{step.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default EstimateList
